import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Check, X, AlertCircle, Loader2, BadgeCheck, Eye, EyeOff, RefreshCw, Bell } from 'lucide-react';
import { toast } from 'sonner';
import ReviewForm from './ReviewForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewsManagementTab = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('pending'); // 'pending', 'all', 'my'
  const [processingId, setProcessingId] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    loadReviews();
    loadNotificationCount();
  }, []);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/reviews/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reviews/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPendingReviews(pendingRes.data || []);
      setAllReviews(allRes.data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Errore nel caricamento delle recensioni');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/review-notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationCount(res.data.count || 0);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  const handleApprove = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recensione approvata e pubblicata!');
      loadReviews();
      loadNotificationCount();
    } catch (error) {
      console.error('Error approving review:', error);
      toast.error('Errore nell\'approvazione della recensione');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/reviews/${reviewId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recensione rifiutata');
      loadReviews();
      loadNotificationCount();
    } catch (error) {
      console.error('Error rejecting review:', error);
      toast.error('Errore nel rifiuto della recensione');
    } finally {
      setProcessingId(null);
    }
  };

  const handleTogglePublish = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/reviews/${reviewId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      loadReviews();
    } catch (error) {
      console.error('Error toggling review:', error);
      toast.error('Errore nella modifica della recensione');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-current text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const ReviewCard = ({ review, showActions = true, isPending = false }) => (
    <div 
      className="bg-white rounded-xl p-5 shadow-sm border"
      style={{ borderColor: isPending ? '#FCD34D' : review.is_published ? '#86EFAC' : '#E5E7EB' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-gray-800">
              {review.user_full_name || review.user_initials}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              review.review_type === 'patient' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-purple-100 text-purple-700'
            }`}>
              {review.review_type === 'patient' ? 'Paziente' : 'Staff'}
            </span>
            {review.is_verified_user && (
              <span className="flex items-center gap-1 text-sky-600 text-xs">
                <BadgeCheck className="w-4 h-4" />
                Verificato
              </span>
            )}
            {review.is_subscribed_user && (
              <span className="flex items-center gap-1 text-amber-600 text-xs">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2"/>
                  <path d="M3 10h18"/>
                </svg>
                Abbonato
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {renderStars(review.rating)}
            <span className="text-xs text-gray-500">{formatDate(review.created_at)}</span>
          </div>
        </div>
        
        {/* Status Badge */}
        <div>
          {isPending ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              In Attesa
            </span>
          ) : review.is_published ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
              <Eye className="w-3 h-3" />
              Pubblicata
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 flex items-center gap-1">
              <EyeOff className="w-3 h-3" />
              Nascosta
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      {review.title && (
        <h4 className="font-semibold text-gray-800 mb-2">"{review.title}"</h4>
      )}
      {review.content && (
        <p className="text-gray-600 text-sm mb-4 italic">"{review.content}"</p>
      )}
      {!review.title && !review.content && (
        <p className="text-gray-400 text-sm mb-4 italic">Solo valutazione, nessun commento</p>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex gap-2 pt-3 border-t border-gray-100">
          {isPending ? (
            <>
              <button
                onClick={() => handleApprove(review.id)}
                disabled={processingId === review.id}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium transition-all disabled:opacity-50"
                data-testid={`approve-review-${review.id}`}
              >
                {processingId === review.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Accetta
              </button>
              <button
                onClick={() => handleReject(review.id)}
                disabled={processingId === review.id}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-all disabled:opacity-50"
                data-testid={`reject-review-${review.id}`}
              >
                {processingId === review.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
                Annulla
              </button>
            </>
          ) : (
            <button
              onClick={() => handleTogglePublish(review.id)}
              disabled={processingId === review.id}
              className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-medium transition-all disabled:opacity-50 ${
                review.is_published 
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' 
                  : 'bg-sky-500 hover:bg-sky-600 text-white'
              }`}
            >
              {processingId === review.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : review.is_published ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Nascondi
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Pubblica
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB'}}>Gestione Recensioni</h2>
          <p className="text-[#2A9FCB]/70">Approva o rifiuta le recensioni dei pazienti e dello staff</p>
        </div>
        <button
          onClick={loadReviews}
          className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 bg-white p-1 rounded-xl shadow-sm">
        <button
          onClick={() => setActiveSubTab('pending')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
            activeSubTab === 'pending' 
              ? 'bg-amber-100 text-amber-800' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          <Bell className="w-4 h-4" />
          In Attesa
          {pendingReviews.length > 0 && (
            <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingReviews.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('all')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
            activeSubTab === 'all' 
              ? 'bg-sky-100 text-sky-800' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          <Star className="w-4 h-4" />
          Tutte ({allReviews.length})
        </button>
        <button
          onClick={() => setActiveSubTab('my')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
            activeSubTab === 'my' 
              ? 'bg-purple-100 text-purple-800' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          La Mia Recensione
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      ) : (
        <>
          {/* Pending Reviews */}
          {activeSubTab === 'pending' && (
            <div>
              {pendingReviews.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Nessuna recensione in attesa</h3>
                  <p className="text-gray-500">Tutte le recensioni sono state gestite</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {pendingReviews.map((review) => (
                    <ReviewCard key={review.id} review={review} isPending={true} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All Reviews */}
          {activeSubTab === 'all' && (
            <div>
              {allReviews.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center">
                  <Star className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Nessuna recensione</h3>
                  <p className="text-gray-500">Non ci sono ancora recensioni nel sistema</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {allReviews.filter(r => r.is_published).length}
                      </p>
                      <p className="text-sm text-green-700">Pubblicate</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {allReviews.filter(r => r.pending_approval).length}
                      </p>
                      <p className="text-sm text-amber-700">In Attesa</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-gray-600">
                        {allReviews.filter(r => !r.is_published && !r.pending_approval).length}
                      </p>
                      <p className="text-sm text-gray-700">Nascoste</p>
                    </div>
                  </div>

                  {/* Review List */}
                  <div className="grid gap-4 md:grid-cols-2">
                    {allReviews.map((review) => (
                      <ReviewCard 
                        key={review.id} 
                        review={review} 
                        isPending={review.pending_approval}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* My Review */}
          {activeSubTab === 'my' && (
            <div className="max-w-lg">
              <div className="mb-4 p-4 rounded-lg bg-purple-50 border border-purple-200">
                <p className="text-sm text-purple-800">
                  Come amministratore, puoi lasciare la tua recensione sull'esperienza lavorativa che apparirà nella sezione "Lavora con Noi".
                </p>
              </div>
              <ReviewForm userRole="admin" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Missing import for CheckCircle - add it
const CheckCircle = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export default ReviewsManagementTab;
